import React from 'react'

// Inline SVG icon set (replaces Font Awesome). Outline glyphs are from
// Heroicons (MIT, https://heroicons.com); the spinner and caret are hand-drawn.
// Every icon is a 24x24 viewBox and inherits its color from `currentColor`,
// so `color`/Tailwind text-* classes style it and `size` scales it (in em).

export type IconProps = {
    className?: string
    color?: string
    /** height & width in em (default 1). e.g. size={3} mirrors FA's size='3x' */
    size?: number
    /** accessible label; when omitted the icon is aria-hidden */
    title?: string
}

type OutlineIconProps = IconProps & { children: React.ReactNode }

function OutlineIcon({ className, color, size = 1, title, children }: OutlineIconProps): JSX.Element {
    return (
        <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            width={`${size}em`}
            height={`${size}em`}
            fill='none'
            stroke='currentColor'
            strokeWidth={1.5}
            strokeLinecap='round'
            strokeLinejoin='round'
            role={title ? 'img' : undefined}
            aria-label={title}
            aria-hidden={title ? undefined : true}
            className={className}
            style={color ? { color } : undefined}>
            {title ? <title>{title}</title> : null}
            {children}
        </svg>
    )
}

export function SpinnerIcon({ className, color, size = 1, title }: IconProps): JSX.Element {
    return (
        <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            width={`${size}em`}
            height={`${size}em`}
            fill='none'
            role={title ? 'img' : undefined}
            aria-label={title}
            aria-hidden={title ? undefined : true}
            className={['animate-spin', className || ''].join(' ').trim()}
            style={color ? { color } : undefined}>
            {title ? <title>{title}</title> : null}
            <circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth={2.5} opacity={0.25} />
            <path d='M12 3a9 9 0 0 1 9 9' stroke='currentColor' strokeWidth={2.5} strokeLinecap='round' />
        </svg>
    )
}

export function CaretDownIcon({ className, color, size = 1, title }: IconProps): JSX.Element {
    return (
        <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            width={`${size}em`}
            height={`${size}em`}
            fill='currentColor'
            role={title ? 'img' : undefined}
            aria-label={title}
            aria-hidden={title ? undefined : true}
            className={className}
            style={color ? { color } : undefined}>
            {title ? <title>{title}</title> : null}
            <path d='M7 10l5 5 5-5z' />
        </svg>
    )
}

export const CheckIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='m4.5 12.75 6 6 9-13.5' /></OutlineIcon>
)

export const XMarkIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='M6 18 18 6M6 6l12 12' /></OutlineIcon>
)

export const BarsIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5' /></OutlineIcon>
)

export const ChevronDownIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='m19.5 8.25-7.5 7.5-7.5-7.5' /></OutlineIcon>
)

export const SignOutIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9' /></OutlineIcon>
)

export const CopyIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75' /></OutlineIcon>
)

export const EyeIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}>
        <path d='M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z' />
        <path d='M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z' />
    </OutlineIcon>
)

export const EyeSlashIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88' /></OutlineIcon>
)

export const EmptyBoxIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z' /></OutlineIcon>
)

export const HomeIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25' /></OutlineIcon>
)

// Brand mark (the cube logo used in the nav).
export function Logo({ size = 44, className }: { size?: number; className?: string }): JSX.Element {
    return (
        <svg aria-label='Gateway' viewBox='0 0 300 300' height={size} width={Math.round((size * 43) / 44)} className={className} xmlns='http://www.w3.org/2000/svg'>
            <path fill='currentColor' d='m234.735 35.532c-8.822 0-16 7.178-16 16s7.178 16 16 16 16-7.178 16-16-7.178-16-16-16zm0 24c-4.412 0-8-3.588-8-8s3.588-8 8-8 8 3.588 8 8-3.588 8-8 8zm-62.529-14c0-2.502 2.028-4.53 4.53-4.53s4.53 2.028 4.53 4.53c0 2.501-2.028 4.529-4.53 4.529s-4.53-2.027-4.53-4.529zm89.059 60c0 2.501-2.028 4.529-4.53 4.529s-4.53-2.028-4.53-4.529c0-2.502 2.028-4.53 4.53-4.53s4.53 2.029 4.53 4.53zm-40.522-5.459-88-51.064c-1.242-.723-2.773-.723-4.016 0l-88 51.064c-1.232.715-1.992 2.033-1.992 3.459v104c0 1.404.736 2.705 1.938 3.428l88 52.936c.635.381 1.35.572 2.062.572s1.428-.191 2.062-.572l88-52.936c1.201-.723 1.938-2.023 1.938-3.428v-104c0-1.426-.76-2.744-1.992-3.459zm-90.008-42.98 80.085 46.47-52.95 31.289-23.135-13.607v-21.713c0-2.209-1.791-4-4-4s-4 1.791-4 4v21.713l-26.027 15.309c-1.223.719-1.973 2.029-1.973 3.447v29.795l-52 30.727v-94.688zm0 198.707-80.189-48.237 51.467-30.412 24.723 14.539v19.842c0 2.209 1.791 4 4 4s4-1.791 4-4v-19.842l26.027-15.307c1.223-.719 1.973-2.029 1.973-3.447v-31.667l52-30.728v94.729z' />
        </svg>
    )
}

// --- Dashboard stat-card icons ---

export const ProfitIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941' /></OutlineIcon>
)

export const MoneyIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z' /></OutlineIcon>
)

export const InvoiceIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z' /></OutlineIcon>
)

export const ReceiptIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='m9 14.25 6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185ZM9.75 9h.008v.008H9.75V9Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm4.125 4.5h.008v.008h-.008V13.5Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z' /></OutlineIcon>
)

export const RefundIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='M8.25 9.75h4.875a2.625 2.625 0 0 1 0 5.25H12M8.25 9.75 10.5 7.5M8.25 9.75 10.5 12m9-7.243V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185Z' /></OutlineIcon>
)

export const PendingIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z' /></OutlineIcon>
)

export const ExpiredIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z' /></OutlineIcon>
)

export const FailedIcon = (p: IconProps): JSX.Element => (
    <OutlineIcon {...p}><path d='m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z' /></OutlineIcon>
)
